import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { palette } from '@ahora-patagonia/design-tokens';
import { IconoChevronDerecha, IconoChevronIzquierda } from '../../../shared/components/iconos.js';

/** El aire entre páginas del riel, en px. Entra en la cuenta del desplazamiento (ver `corrimiento`). */
const GAP = 20;
const VIAJE = '400ms cubic-bezier(.4,0,.2,1)';

/**
 * Solo para el lector de pantalla: el aviso de cambio de página que antes daba el "1 de N".
 *
 * Las medidas van en `px` **como string**: en `sx` un número ≤ 1 en `width`/`height` es una
 * proporción (`1` = `100%`), así que `width: 1` deja una caja del ancho de la página que desborda
 * el documento y saca la barra horizontal.
 */
const SOLO_LECTOR = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

export interface CarruselDelBloqueProps {
  /** Una entrada por página, ya dibujada con la plantilla del bloque. */
  paginas: ReactNode[];
  pagina: number;
  onCambiar: (pagina: number) => void;
}

/**
 * Cuánto se corre el riel para dejar a la vista la página `pagina`.
 *
 * Una página mide el ancho del contenedor **más el gap**: el `-100%` pelado se come ese aire y
 * deja el riel 20 px corrido por salto (se nota como media tarjeta asomando). El porcentaje lo
 * resuelve el ancho del riel, que es el del contenedor —las páginas se desbordan, no lo
 * ensanchan—, así que la cuenta es exacta en cualquier corte.
 */
function corrimiento(pagina: number): string {
  if (pagina <= 0) return 'translateX(0px)';
  return `translateX(calc(-100% * ${pagina} - ${GAP}px * ${pagina}))`;
}

/**
 * El riel de un bloque con más notas de las que entran de una vez: las páginas viajan de costado y
 * los chevrones viven en las columnas laterales, **fuera** del área de las tarjetas (nunca encima
 * de una foto) y centrados contra la fila entera.
 *
 * Navegación **dentro** del bloque: las notas ya vinieron todas en la respuesta de la home, así que
 * esto no pide nada, no toca la URL y no mueve el scroll. Como todas las páginas están en el DOM
 * (es lo que permite el viaje), las que no se ven van con `visibility: hidden` —fuera del tabulado
 * y del lector— pero recién al terminar la transición, si no la página que sale desaparecería a
 * mitad de camino.
 *
 * En los extremos el chevrón se **deshabilita, no se esconde**: si desaparece, el lector no
 * entiende por qué.
 */
export function CarruselDelBloque({ paginas, pagina, onCambiar }: CarruselDelBloqueProps) {
  const primera = pagina === 0;
  const ultima = pagina >= paginas.length - 1;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', sm: '12px' } }}>
      <Flecha sentido="anterior" deshabilitada={primera} onClick={() => onCambiar(pagina - 1)} />
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Box
          data-riel
          // El transform va inline y no en `sx`: cambia en cada página y no tiene sentido
          // generar una clase de emotion por cada una.
          style={{ transform: corrimiento(pagina) }}
          sx={{ display: 'flex', gap: `${GAP}px`, transition: `transform ${VIAJE}` }}
        >
          {paginas.map((contenido, i) => {
            const activa = i === pagina;
            return (
              <Box
                key={i}
                aria-hidden={activa ? undefined : true}
                sx={{
                  flex: '0 0 100%',
                  minWidth: 0,
                  visibility: activa ? 'visible' : 'hidden',
                  transition: activa ? 'visibility 0s' : 'visibility 0s 400ms',
                }}
              >
                {contenido}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Flecha sentido="siguiente" deshabilitada={ultima} onClick={() => onCambiar(pagina + 1)} />
      <Box aria-live="polite" sx={SOLO_LECTOR}>
        Página {pagina + 1} de {paginas.length}
      </Box>
    </Box>
  );
}

/**
 * El chevrón suelto: sin fondo, sin borde y sin caja. Lo que se ve es el glifo, apagado a la
 * mitad hasta que el mouse encima lo enciende en el rojo del sistema.
 */
function Flecha({
  sentido,
  deshabilitada,
  onClick,
}: {
  sentido: 'anterior' | 'siguiente';
  deshabilitada: boolean;
  onClick: () => void;
}) {
  const anterior = sentido === 'anterior';

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={deshabilitada}
      aria-label={anterior ? 'Anterior' : 'Siguiente'}
      sx={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        p: 0,
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        color: palette.ink,
        opacity: 0.5,
        transition: 'opacity 150ms',
        '&:hover': { opacity: 1, color: palette.systemRed },
        '&:focus-visible': { outline: `2px solid ${palette.systemRed}`, outlineOffset: '2px' },
        '&:disabled': { opacity: 0.2, pointerEvents: 'none' },
      }}
    >
      {anterior ? <IconoChevronIzquierda /> : <IconoChevronDerecha />}
    </Box>
  );
}
